import { Vector3 } from '@babylonjs/core';

import type { Grabber } from './Grabbers';
import type { LobbyView } from '../room/LobbyView';

/** How far the thumbstick has to go before a rank moves. */
const ROTATE_DEADZONE = 0.6;

/** How close the aim ray has to pass to a miniature's centre to count as pointing at it. */
const AIM_TOLERANCE_METRES = 0.35;

/** Nothing closer than this counts — a miniature behind the viewer is not being aimed at. */
const MIN_AIM_DISTANCE = 0.2;

/**
 * Reaching the lobby: rotating a rank forward, and opening the diagram you are pointing at
 * (SPEC-162, SPEC-156).
 *
 * **Held to the same rule as every other gesture in this project: the mode decides what a press
 * means, and nothing is ambiguous by distance** (`plan/mode-hierarchy.md`). The lobby is only
 * interactive while the room shows no diagram, which is a state, not a proximity — so a trigger
 * press can never mean both "open this diagram" and "grab that node". When a diagram is open the
 * lobby is not merely hidden, it is not listening.
 *
 * Ranks move on a discrete thumbstick push rather than continuously, matching the layout: a
 * diagram is either in the near arc or it is not, and a continuous spin would leave miniatures
 * stopped between ranks at sizes nobody chose.
 */
// spec: SPEC-156
// spec: SPEC-162
export class LobbyInput {
  private active = false;

  /** True while the stick is held past the deadzone, so one push moves exactly one rank. */
  private stickLatched = false;

  constructor(
    private readonly lobby: LobbyView,
    /** Opens a diagram in the room the person is already standing in (SPEC-161). */
    private readonly open: (diagramId: string) => void,
  ) {}

  /**
   * Whether the lobby is the interactive surface right now. Set from the room's own diagram
   * pointer, so it is the same fact the lobby's visibility is driven from rather than a second
   * one that could disagree.
   */
  setActive(active: boolean): void {
    this.active = active;

    if (!active) {
      this.stickLatched = false;
    }
  }

  get isActive(): boolean {
    return this.active;
  }

  /**
   * One frame. Returns whether the lobby consumed the grabber's press, so the caller can keep
   * arbitration in one place rather than having each gesture guess.
   */
  tick(grabber: Grabber): boolean {
    if (!this.active || grabber.role !== 'dominant') {
      return false;
    }

    this.tickRotation(grabber);

    if (!grabber.far.justPressed) {
      return false;
    }

    const aimed = this.diagramUnderAim(grabber);

    if (aimed === null) {
      return false;
    }

    this.open(aimed);
    return true;
  }

  private tickRotation(grabber: Grabber): void {
    const x = grabber.stick?.x ?? 0;

    if (Math.abs(x) < ROTATE_DEADZONE) {
      // Released far enough back to arm the next push. Without this the lobby would race through
      // every rank while the stick was held over.
      this.stickLatched = false;
      return;
    }

    if (this.stickLatched) {
      return;
    }

    this.stickLatched = true;
    this.lobby.rotate(x > 0 ? 1 : -1);
  }

  /**
   * Which miniature the aim ray passes nearest to, or null.
   *
   * Perpendicular distance to the ray rather than a scene pick, deliberately: a miniature is a
   * cloud of small meshes with gaps between them, so a ray pick would land on a shape or slip
   * between two and report nothing, and pointing "at the diagram" would work or not depending on
   * whether a node happened to be in the way. The slot centre is what a person is actually
   * aiming at.
   */
  private diagramUnderAim(grabber: Grabber): string | null {
    let best: string | null = null;
    let bestDistance = Infinity;

    for (const diagramId of this.lobby.builtDiagramIds) {
      const centre = this.lobby.centreOf(diagramId);

      if (centre === null) {
        continue;
      }

      const toCentre = centre.subtract(grabber.aimOrigin);
      const along = Vector3.Dot(toCentre, grabber.forward);

      if (along < MIN_AIM_DISTANCE) {
        continue;
      }

      const closest = grabber.aimOrigin.add(grabber.forward.scale(along));
      const offAxis = Vector3.Distance(closest, centre);

      if (offAxis > AIM_TOLERANCE_METRES) {
        continue;
      }

      // Nearest along the ray wins, not nearest to it: pointing through a near miniature at one
      // behind it should open the near one, which is what a person means by pointing.
      if (along < bestDistance) {
        best = diagramId;
        bestDistance = along;
      }
    }

    return best;
  }
}
